import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Row, Column, DescriptiveText, Button, Carousel} from '../../shared';
import {MgpPoS, EMoneyPoS} from '../../shared';
import useSWR from 'swr';
import {_openModal} from '../../shared/reducers/modals';
import {SizeMe} from 'react-sizeme';
import axiosInstance from '../../shared/utils/axios';
import {open} from '../../shared/utils/utils';
import styled from 'styled-components/macro';

const _Wrapper = styled.div`
  width: 100%;
  height: 100%;
`;

const _LayoutSwitcher = ({count, children}) => {
  if (count === 1) {
    return <Row height={'100%'}>{children}</Row>;
  }
  return <Column height={'100%'}>{children}</Column>;
};
_LayoutSwitcher.propTypes = {
  count: PropTypes.number,
  children: PropTypes.node,
};

const ProbabilityOfSuccess = (props) => {
  const {context: {id: householdID}, session, openModal, expanded} = props;
  const brandedName = session.profile.lowerCaseSingularAdvisorTerm || 'advisor';
  const brandedNamePlural = session.profile.lowerCasePluralAdvisorTerm || 'advisors';
  const [showMgp, setShowMgp] = useState(false);
  const [mgpPoS, setMgpPoS] = useState({text: 'Financial Plan Confidence Level', percent: '?'});

  const {data: advisors} = useSWR('/user/advisors');

  const probability = advisors && advisors[0].financialPlanning.emoney?.successPercent;
  const displayText = advisors && advisors[0].financialPlanning.emoney?.successTargetText;
  const error = displayText ? displayText.split(':').includes('Error') : null;
  const eMoneyPoS = probability ? Number(probability.substring(0, probability.length - 1)) : null;

  const {data: collections} = useSWR(`/preferences/users/${householdID}/panels/investorGeneralWeb`);
  const investorFinancialPlanningCollection = collections?.collections
    .find(collection => collection.title === 'investorFinancialPlanningWeb');
  const investorFinancialPlanningGroup = investorFinancialPlanningCollection?.groups
    .find(group => group.title === 'investorFinancialPlanningWeb');
  const investorFinancialPlanningAdvisorCore = investorFinancialPlanningGroup?.cores
    .find(core => core.title === 'investorFinancialPlanningAdvisorWeb');

  const advisorDefault = investorFinancialPlanningAdvisorCore?.val;
  const advisorOptions = investorFinancialPlanningAdvisorCore?.options;
  const options = advisorOptions?.map(e => ({label: e.displayName, value: e.id}));

  const [selectedAdvisor, setSelectedAdvisor] = useState();
  useEffect(() => {
    setSelectedAdvisor(advisorDefault);
  }, [advisorDefault]);

  useEffect(() => {
    const advisor = advisors?.find(x => x.id === selectedAdvisor);
    setMgpPoS({
      text: advisor?.financialPlanning?.successTargetText,
      percent: advisor?.financialPlanning?.successPercent,
    });
    if (investorFinancialPlanningAdvisorCore) {
      setShowMgp(true);
    } else {
      setShowMgp(false);
    }
  }, [selectedAdvisor]);

  const selectedAdvisorName = options?.find(a => a.value === selectedAdvisor)?.label || 'your ' + brandedName;

  const mgpLaunch = () => axiosInstance.get(`/auth/mgp/login/guest/${selectedAdvisor}`)
    .then(res => open('POST', res.data.postUrl, {RelayState: res.data.RelayState, SAMLResponse: res.data.SAMLResponse}, '_blank'))
    .catch(err => openModal({type: 'Error', title: 'MoneyGuide Error', err}));

  const showEMoneyPoS = !error && !!eMoneyPoS;
  const count = showMgp + showEMoneyPoS;

  return <_Wrapper>
    <SizeMe monitorHeight={true}>
      {({size}) => {
        let moreText;
        if (options?.length > 1) {
          if (count === 1 || size.width > 415) {
            moreText = ` Click “Change View” to view Financial Planning data from your other ${brandedName}.`;
          }
        }
        const showLogos = expanded || !(count > 1 && size.height < 275);

        return <_LayoutSwitcher count={count}>
          <Column width={count === 1 && '50%'}
                  height={'100%'}
                  maxHeight={expanded ? '280px' : '200px'}
                  justifySubColumn={'middle'}
                  alignItems={'center'}
                  overflow={'hidden'}>
            <Carousel height={'100%'}
                      childMinWidth={count === 1 ? '130px' : '200px'}
                      // eslint-disable-next-line no-nested-ternary
                      childMinHeight={showLogos
                      // eslint-disable-next-line no-nested-ternary
                        ? expanded
                          ? '280px'
                          : size.width > 270 ? '200px' : '165px'
                        : '130px' }>
              {showMgp ? <MgpPoS probabilityOfSuccess={mgpPoS}
                                 showLogo={showLogos}
                                 // eslint-disable-next-line no-nested-ternary
                                 layoutSize={expanded
                                   // eslint-disable-next-line no-nested-ternary
                                   ? size.width > 425
                                     ? 'md'
                                     : size.width > 285 ? 'sm' : 'xs'
                                   : size.width > 285 ? 'sm' : 'xs'}
                                 action={session.profile.mgpClientPortalAccessible ? () => mgpLaunch() : null}/> : null}
              {showEMoneyPoS ? <EMoneyPoS probabilityOfSuccess={eMoneyPoS}
                                          layoutSize={expanded ? 'md' : 'sm'}
                                          showLogo={showLogos}/> : null}
            </Carousel>
          </Column>
          <Column width={count === 1 && '50%'} overflow={'hidden'} layout={{shrink: 0}}>
            <DescriptiveText>Plan numbers are based on accounts {selectedAdvisorName} manages.{moreText}</DescriptiveText>
            {options?.length > 1
              ? <Button label={'Change View'}
                        importance={'secondary'}
                        size={'md'}
                        action={() => openModal({
                          componentName: 'ChangeViewAdvisor',
                          householdID,
                          selectedAdvisor,
                          options,
                          brandedName,
                          brandedNamePlural,
                          updateSelected: id => setSelectedAdvisor(id),
                        })}/>
              : null}
          </Column>
        </_LayoutSwitcher>;
      }}
    </SizeMe>
  </_Wrapper>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProbabilityOfSuccess);

ProbabilityOfSuccess.propTypes = {
  session: PropTypes.shape({
    profile: PropTypes.shape({
      lowerCaseSingularAdvisorTerm: PropTypes.string,
      lowerCasePluralAdvisorTerm: PropTypes.string,
      mgpClientPortalAccessible: PropTypes.bool,
    }),
  }).isRequired,
  openModal: PropTypes.func.isRequired,
  context: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  /**
   * Whether or not the Widget is being viewed on a very large viewport.
   */
  expanded: PropTypes.bool,
};
